import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { subscriptionsService, BillingSettings, Subscription } from '@/services/subscriptions';
import Button from '@/components/ui/Button';
import {
  Loader2,
  Save,
  CheckCircle,
  Clock,
  Building2,
  CreditCard,
} from 'lucide-react';
import { formatPrice, formatDate } from '@/utils/format';
import toast from 'react-hot-toast';

export default function AdminBillingSettings() {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery<BillingSettings>({
    queryKey: ['admin', 'billing-settings'],
    queryFn: subscriptionsService.getBillingSettings,
  });

  const { data: pendingPayments = [] } = useQuery<Subscription[]>({
    queryKey: ['admin', 'pending-payments'],
    queryFn: subscriptionsService.listPendingPayments,
    refetchInterval: 60_000,
  });

  const [formData, setFormData] = useState<BillingSettings | null>(null);

  useEffect(() => {
    if (settings) setFormData(settings);
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<BillingSettings>) => subscriptionsService.updateBillingSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'billing-settings'] });
      toast.success('Configuración guardada');
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'Error al guardar'),
  });

  const confirmMutation = useMutation({
    mutationFn: ({ subscriptionId, notes }: { subscriptionId: number; notes?: string }) =>
      subscriptionsService.confirmPayment(subscriptionId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'pending-payments'] });
      toast.success('Pago confirmado');
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'Error al confirmar'),
  });

  if (isLoading || !formData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { id, ...data } = formData;
    updateMutation.mutate(data);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configuración de cobro</h1>
        <p className="text-gray-500">Datos bancarios que los almacenes ven al pagar su suscripción</p>
      </div>

      {/* Pagos pendientes de confirmar */}
      {pendingPayments.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
          <h2 className="flex items-center gap-2 font-semibold text-blue-900 mb-4">
            <Clock className="h-5 w-5" />
            Pagos reportados pendientes de confirmar ({pendingPayments.length})
          </h2>
          <div className="space-y-2">
            {pendingPayments.map((sub) => (
              <div key={sub.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-100">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900">Suscripción #{sub.id}</p>
                    <p className="text-xs text-gray-500">
                      Reportó el {formatDate(sub.payment_reported_at!)} · {formatPrice(sub.price_monthly)}
                      {sub.payment_reported_reference && ` · Ref: ${sub.payment_reported_reference}`}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => {
                    if (confirm(`¿Confirmás la recepción del pago de ${formatPrice(sub.price_monthly)}?`)) {
                      confirmMutation.mutate({ subscriptionId: sub.id });
                    }
                  }}
                  disabled={confirmMutation.isPending}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Confirmar
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form de datos bancarios */}
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
            <CreditCard className="h-5 w-5 text-indigo-600" />
          </div>
          <h2 className="font-semibold text-gray-900">Datos bancarios globales</h2>
        </div>

        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-900">
          <strong>Al menos uno de los dos (alias o CBU) es obligatorio.</strong> Podés cargar ambos si querés.
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Alias <span className="text-xs text-gray-400 font-normal">(o CBU)</span>
            </label>
            <input
              type="text"
              value={formData.alias || ''}
              onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
              placeholder="inventa.mp"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              CBU <span className="text-xs text-gray-400 font-normal">(o alias)</span>
            </label>
            <input
              type="text"
              value={formData.cbu || ''}
              onChange={(e) => setFormData({ ...formData, cbu: e.target.value })}
              placeholder="0000003100000000000000"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titular</label>
            <input
              type="text"
              value={formData.account_holder || ''}
              onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
              placeholder="Juan Pérez"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Banco</label>
            <input
              type="text"
              value={formData.bank_name || ''}
              onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
              placeholder="Banco Galicia"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CUIT (opcional)</label>
            <input
              type="text"
              value={formData.cuit || ''}
              onChange={(e) => setFormData({ ...formData, cuit: e.target.value })}
              placeholder="20-12345678-9"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp (para avisos de pago)</label>
            <input
              type="tel"
              value={formData.whatsapp_number || ''}
              onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
              placeholder="+5491122334455"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
            <p className="text-xs text-gray-500 mt-1">Incluí código de país. Los tenants te avisarán a este número cuando paguen.</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Instrucciones adicionales (opcional)</label>
          <textarea
            rows={3}
            value={formData.payment_instructions || ''}
            onChange={(e) => setFormData({ ...formData, payment_instructions: e.target.value })}
            placeholder="Ej: Transferí el monto indicado. Una vez realizada la transferencia, avisanos por WhatsApp enviando el comprobante."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          />
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={updateMutation.isPending}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {updateMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Guardar configuración
          </Button>
        </div>
      </form>
    </div>
  );
}
